import { useCallback, useMemo, useRef, useState } from 'react';
import type { FieldErrors } from 'react-hook-form';

import { LeaseDetailsForm } from '@/modules/application-tracker/components/lease-form';
import type { CoveragePricing } from '@/shared/types';
import { checkIfPmi } from '@/utils/commonUtils';
import { MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE } from '@/utils/constants';

import { inviteLeaseDetailsPreset } from './invite-lease-details.preset';
import { startInviteSubmitAttempt } from './invite-lease-details.rum';
import {
  useHasCustomScreeningStatusLabels,
  useInviteFormTracking,
  useInviteTracking,
} from './hooks';
import type { UseInviteLeaseDetailsControllerResult } from './hooks';
import type { InviteFlowMilestoneKey } from './types';

export type InviteLeaseDetailsSharedFormProps = {
  /** Hoisted by the drawer so legacy and shared share one controller instance. */
  controller: UseInviteLeaseDetailsControllerResult;
  onMilestoneReached?: (key: InviteFlowMilestoneKey) => void;
};

// Config-native invite drawer (OTTERS-4671). Builds `inviteLeaseDetailsPreset`
// and hands it to the shared `<LeaseDetailsForm>`; the engine renders every
// section/field, derives the schema from config, and owns the RHF lifecycle.
// This component only assembles the preset deps and wires flow-start /
// validation-error analytics.
export function InviteLeaseDetailsSharedForm({
  controller,
  onMilestoneReached,
}: InviteLeaseDetailsSharedFormProps) {
  const { user, errorMsg, isSmbInvite, useNewForm, customCoverageDisabled, onSubmit } = controller;

  // address.source-derived; lifted from the effects component so the SDR cap stays reactive.
  const [isSelfEnrolled, setIsSelfEnrolled] = useState(false);
  // Written by the effects component, read by the preset's `buildPayload`.
  const selectedSmbPricingRef = useRef<CoveragePricing | undefined>(undefined);

  const isPmi = Boolean(user && checkIfPmi(user));

  const selfEnrolledSDRLimit = useMemo(() => {
    if (!user) {
      return undefined;
    }
    if (checkIfPmi(user)) {
      return MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE.PMI;
    }
    if (isSelfEnrolled) {
      return MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE.SELF_ENROLLED;
    }
    return undefined;
  }, [user, isSelfEnrolled]);

  const { trackInvite, flowId, entryPoint, inviteSegment } = useInviteTracking();
  const startSubmitAttempt = useCallback(
    (attemptNumber: number, applicantCount: number) =>
      startInviteSubmitAttempt({
        flowId,
        entryPoint,
        inviteSegment,
        attemptNumber,
        applicantCount,
      }),
    [flowId, entryPoint, inviteSegment],
  );
  const hasCustomScreeningStatusLabels = useHasCustomScreeningStatusLabels(isSmbInvite);
  const { trackFlowStarted, trackValidationErrors } = useInviteFormTracking({
    onFlowStarted: () => onMilestoneReached?.('hadFlowStarted'),
  });

  const handleValidationError = useCallback(
    (errors: FieldErrors) => {
      onMilestoneReached?.('hadSubmitAttempt');
      trackValidationErrors(errors, 'client');
    },
    [onMilestoneReached, trackValidationErrors],
  );

  const config = useMemo(
    () =>
      inviteLeaseDetailsPreset({
        isSmbInvite,
        isPmi,
        newCoveragesEnabled: useNewForm,
        customCoverageDisabled,
        selfEnrolledSDRLimit,
        errorMsg,
        trackInvite,
        onMilestoneReached,
        onSelfEnrolledChange: setIsSelfEnrolled,
        hasCustomScreeningStatusLabels,
        selectedSmbPricingRef,
        startSubmitAttempt,
        onSubmit,
      }),
    [
      isSmbInvite,
      isPmi,
      useNewForm,
      customCoverageDisabled,
      selfEnrolledSDRLimit,
      errorMsg,
      trackInvite,
      onMilestoneReached,
      hasCustomScreeningStatusLabels,
      startSubmitAttempt,
      onSubmit,
    ],
  );

  return (
    <div
      onFocusCapture={() => trackFlowStarted('field_focus')}
      onClickCapture={() => trackFlowStarted('click')}
      onKeyDownCapture={() => trackFlowStarted('typing')}
    >
      <LeaseDetailsForm config={config} mode="all" onValidationError={handleValidationError} />
    </div>
  );
}
